import React, { useMemo, useState } from 'react';
import { Copy, Plus, RefreshCw, Trash2, Ban } from 'lucide-react';
import { useSignupCodes } from 'hooks';
import { SignupCodeResponse } from 'services/api';
import { useToast } from 'contexts/ToastContext';
import { GenerateSignupCodeModal } from 'components/modals';

const SignupCodes: React.FC = () => {
  const { codes, loading, error, refreshData, generateSignupCode, revokeSignupCode, deleteSignupCode } = useSignupCodes();
  const toast = useToast();
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedCode, setGeneratedCode] = useState<SignupCodeResponse | null>(null);

  const stats = useMemo(() => {
    return {
      active: codes.filter((code) => code.status === 'active').length,
      revoked: codes.filter((code) => code.status === 'revoked').length,
      exhausted: codes.filter((code) => code.status === 'exhausted').length,
      expired: codes.filter((code) => code.status === 'expired').length,
    };
  }, [codes]);

  const statusStyle: Record<SignupCodeResponse['status'], string> = {
    active: 'bg-green-100 text-green-800',
    revoked: 'bg-red-100 text-red-800',
    exhausted: 'bg-yellow-100 text-yellow-800',
    expired: 'bg-gray-100 text-gray-800',
  };

  const handleCopy = async (value: string) => {
    await navigator.clipboard.writeText(value);
    toast.success('Signup code copied');
  };

  const handleGenerate = async ({ expiresInDays, maxUses }: { expiresInDays: number; maxUses: number }) => {
    try {
      setIsGenerating(true);
      const result = await generateSignupCode(expiresInDays, maxUses);
      setGeneratedCode(result.data || null);
      toast.success('Signup code generated successfully');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to generate signup code');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRevoke = async (code: SignupCodeResponse) => {
    if (!window.confirm(`Revoke code ${code.code}? Employees will not be able to use it.`)) {
      return;
    }
    try {
      await revokeSignupCode(code.id);
      toast.success('Signup code revoked');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to revoke signup code');
    }
  };

  const handleDelete = async (code: SignupCodeResponse) => {
    if (!window.confirm(`Delete unused code ${code.code}? This action cannot be undone.`)) {
      return;
    }
    try {
      await deleteSignupCode(code.id);
      toast.success('Signup code deleted');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete signup code');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Signup Codes</h1>
          <p className="text-gray-600">Generate and manage employee signup codes</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={refreshData} className="btn-secondary inline-flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button
            onClick={() => {
              setGeneratedCode(null);
              setIsGenerateOpen(true);
            }}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Generate Code
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-sm text-gray-600">Active</p>
          <p className="text-2xl font-semibold text-gray-900">{stats.active}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600">Revoked</p>
          <p className="text-2xl font-semibold text-gray-900">{stats.revoked}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600">Exhausted</p>
          <p className="text-2xl font-semibold text-gray-900">{stats.exhausted}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600">Expired</p>
          <p className="text-2xl font-semibold text-gray-900">{stats.expired}</p>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="py-10 text-center text-gray-600">Loading signup codes...</div>
        ) : error ? (
          <div className="py-10 text-center">
            <p className="text-red-600 mb-3">{error}</p>
            <button onClick={refreshData} className="btn-primary">Try Again</button>
          </div>
        ) : codes.length === 0 ? (
          <div className="py-10 text-center text-gray-600">
            No signup codes yet. Generate your first code to onboard employees.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Code</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Usage</th>
                  <th className="table-header">Expires</th>
                  <th className="table-header">Created</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {codes.map((code) => {
                  const usagePct = Math.min(100, (code.usedCount / code.maxUses) * 100);

                  return (
                    <tr key={code.id} className="hover:bg-gray-50">
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <code className="text-sm font-semibold tracking-wider">{code.code}</code>
                          <button
                            onClick={() => handleCopy(code.code)}
                            className="text-gray-400 hover:text-primary-600"
                            title="Copy code"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${statusStyle[code.status]}`}>
                          {code.status}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div>
                          <p className="text-sm text-gray-900">{code.usedCount} / {code.maxUses}</p>
                          <div className="w-36 bg-gray-200 rounded-full h-2 mt-1">
                            <div className="bg-primary-600 h-2 rounded-full" style={{ width: `${usagePct}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">{new Date(code.expiresAt).toLocaleDateString()}</td>
                      <td className="table-cell">{new Date(code.createdAt).toLocaleDateString()}</td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleRevoke(code)}
                            disabled={code.status !== 'active'}
                            className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                            title={code.status !== 'active' ? 'Only active codes can be revoked' : 'Revoke code'}
                          >
                            <Ban className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(code)}
                            disabled={code.usedCount > 0}
                            className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                            title={code.usedCount > 0 ? 'Cannot delete a used code; revoke instead' : 'Delete code'}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <GenerateSignupCodeModal
        isOpen={isGenerateOpen}
        loading={isGenerating}
        generatedCode={generatedCode}
        onClose={() => setIsGenerateOpen(false)}
        onGenerate={handleGenerate}
      />
    </div>
  );
};

export default SignupCodes;
